import { useState } from "react";
import { initials, isRealLogo, logoSources } from "./logos";

// Which source URLs turned out to be real logos (true) or a service's generic
// "no icon" (false), for this page load, so a card that re-mounts (search,
// drag, reload) shows its answer at once instead of flashing initials again.
const verdicts = new Map<string, boolean>();

export type LogoSize = "sm" | "md" | "lg";

/**
 * A company's logo, from its website; initials in Masora tokens when there is
 * no website or no source has a real logo. The initials sit underneath and the
 * image is only revealed once it has loaded and passed isRealLogo, so a
 * service's generic answer is never shown as if it were the firm's.
 * Decorative: the company's name is always next to it.
 */
export function CompanyLogo({ name, website, size = "md" }: { name: string; website: string | null; size?: LogoSize }) {
  const sources = logoSources(website).filter((s) => verdicts.get(s) !== false);
  // Keyed on the sources, so editing the website starts the lookup afresh.
  return <Tile key={sources.join(" ")} name={name} sources={sources} size={size} />;
}

function Tile({ name, sources, size }: { name: string; sources: string[]; size: LogoSize }) {
  const [i, setI] = useState(0);
  const src = sources[i];
  const [shown, setShown] = useState(() => src !== undefined && verdicts.get(src) === true);
  const next = () => { verdicts.set(src!, false); setShown(false); setI(i + 1); };
  return (
    <span className={`logo ${size}${shown ? " has-img" : ""}`} data-logo={shown ? "image" : "initials"} aria-hidden="true">
      <span className="logo-initials">{initials(name)}</span>
      {src ? (
        <img src={src} alt="" referrerPolicy="no-referrer" decoding="async" draggable={false}
          onLoad={(e) => {
            if (!isRealLogo(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)) return next();
            verdicts.set(src, true);
            setShown(true);
          }}
          onError={next} />
      ) : null}
    </span>
  );
}

/** The same tile for a person: initials only, never an image. */
export function PersonTile({ name, size = "sm" }: { name: string; size?: LogoSize }) {
  return <span className={`logo ${size} person`} aria-hidden="true"><span className="logo-initials">{initials(name)}</span></span>;
}
